import {
  NodeMigrationStatusMigrationStatusEnum,
  NormalizedAutoscalerRebalancingPlan,
  NormalizedKentRebalancingPlan,
  RebalancingNode,
  WorkloadIssue,
  WorkloadMigrationStatusMigrationStatusEnum,
  RebalancingPlanStatusEnum,
  KentRebalancingPlanStatusEnum,
  RebalancingJob,
  RebalancingSchedule,
  AggressiveModeConfig,
} from '@cast/types';
import {
  KentAggressiveModeConfig,
  KentRebalancingSchedule,
} from '@cast/types-kent';

export type AggregatedRebalancingNode = RebalancingNode & {
  quantity: number;
  totalMemoryGib: number;
  totalCPU: number;
  priceMonthly: number;
  blendedHourlyPricePerCpu: number;
  problematic: boolean;
  affected: boolean;
  original: RebalanceNodeTableEntry[];
};

export type RebalanceNodeTableEntry = {
  id?: string;
  name: string;
  instanceType: string;
  status: NodeMigrationStatusMigrationStatusEnum;
  pods: number;
  problematicPods: number;
  createdAt?: string;
  createdBy?: string;
};

export enum RebalanceNodeEnum {
  READY = WorkloadMigrationStatusMigrationStatusEnum.ready,
  NOT_READY = WorkloadMigrationStatusMigrationStatusEnum.not_ready,
}

export type RebalanceNodeWorkload = {
  name: string;
  namespace: string;
  resource: string;
  replicas: number;
  issues?: WorkloadIssue[];
  nodes: Array<{ id: string; name: string }>;
  status: WorkloadMigrationStatusMigrationStatusEnum;
};

export type RebalancePlanSimulationProgress = {
  simulatedProgress: number;
  overallSimulatedProgress: number;
};

type RebalanceProgressMetric = {
  percentage: number;
  operations: {
    completed: number;
    total: number;
  };
};

export type RebalanceProgressPercentage = {
  create: RebalanceProgressMetric | null;
  cleanup: RebalanceProgressMetric | null;
  overall: RebalanceProgressMetric | null;
};

export type ClusterSchedule = RebalancingJob & {
  name: string;
  schedule: RebalancingSchedule;
  targetUtcMinutes?: number;
};

export type DerivedRebalancingPlanStatus = 'obsolete';

// Fully built plan item except for derivedStatus, which requires cross-plan
// context to determine obsolescence. Built in two stages: first as this base
// type, then partitionRebalancingPlans adds derivedStatus to produce the
// final RebalancingPlanListItem that consumers receive. RebalancingPlanListItemBase should not be used directly.
export type RebalancingPlanListItemBase =
  | {
      variant: 'autoscaler';
      normalizedRebalancingPlan: NormalizedAutoscalerRebalancingPlan;
      schedule?: ClusterSchedule;
    }
  | {
      variant: 'kent';
      normalizedRebalancingPlan: NormalizedKentRebalancingPlan;
      schedule?: KentRebalancingSchedule;
    };

export type RebalancingPlanListItem = RebalancingPlanListItemBase & {
  derivedStatus: DerivedRebalancingPlanStatus | null;
};

export type RebalancePlanStatusDisplay =
  | { variant: 'autoscaler'; status: RebalancingPlanStatusEnum }
  | { variant: 'kent'; status: KentRebalancingPlanStatusEnum }
  | { variant: 'derived'; status: DerivedRebalancingPlanStatus };

export enum RebalanceDashboardTabs {
  LOG = 'log',
  SCHEDULE = 'schedule',
}

export enum RebalancingPlanCreationSubRoutes {
  CONFIGURE = 'configure',
  REVIEW = 'review',
}

export enum RebalancePlanReportTabs {
  ACHIEVED = 'achieved',
  PREDICTED = 'predicted',
}

export type RebalancingPlanDataScope = 'achieved' | 'predicted';
export type RebalancingPlanConfigurationType = 'blue' | 'green';

type KentAggressiveModeSettings = {
  type: 'kent';
  config: KentAggressiveModeConfig;
};

type AutoscalerAggressiveModeSettings = {
  type: 'autoscaler';
  config: AggressiveModeConfig;
};

export type AggressiveModeSettings =
  | KentAggressiveModeSettings
  | AutoscalerAggressiveModeSettings;
